import { Link } from '@/components/Link';
import { at } from '@/util/at';
import { clamp } from '@/util/clamp';
import { RouteType } from '@/util/routeToPath';
import { ReactNode } from 'react';
import styles from './Pagination.module.scss';

type Router = (page: number) => RouteType;
type OnChangeOffset = (offset: number) => void;

type Props = {
  offset: number;
  last: number; // Last offset
  route: Router; // Render ssr link
  onChangeOffset?: OnChangeOffset; // Callback to hijack navigation
};

export function Pagination({ offset, last, route, onChangeOffset }: Props) {
  const numPages = last + 1;
  const padding = 1;
  const preferredAmount = 1 + padding * 2;

  let min = clamp(offset - padding, 0, numPages);
  const max = clamp(min + preferredAmount, 0, numPages);

  if (max > preferredAmount) {
    min = max - preferredAmount;
  }

  const hasPrevButton = offset > 0;
  const hasNextButton = offset < last;

  const range = max - min;
  const offsets = Array.from({ length: range }, (_, i) => min + i);

  if (last < 1) {
    return null;
  }

  return (
    <nav className={styles.Pagination} aria-label="Paginering">
      {hasPrevButton ? (
        <PageLink
          offset={offset - 1}
          route={route}
          onChangeOffset={onChangeOffset}
          rel="prev"
          withAriaLabel={false}
        >
          Vorige pagina
        </PageLink>
      ) : (
        <span />
      )}

      {offset > padding ? (
        <>
          <PageLink offset={0} route={route} onChangeOffset={onChangeOffset}>
            1
          </PageLink>
          <span>...</span>
        </>
      ) : (
        <>
          {/* <span /> */}
          {/* <span /> */}
        </>
      )}

      {offsets.map((index) => (
        <PageLink
          key={index}
          offset={index}
          route={route}
          current={offset === index}
          onChangeOffset={onChangeOffset}
        />
      ))}

      {offset < last - padding && last !== at(offsets, -1) ? (
        <>
          <span>...</span>
          <PageLink offset={last} route={route} onChangeOffset={onChangeOffset}>
            {last + 1}
          </PageLink>
        </>
      ) : (
        <>
          {/* <span /> */}
          {/* <span /> */}
        </>
      )}

      {hasNextButton ? (
        <PageLink
          offset={offset + 1}
          route={route}
          onChangeOffset={onChangeOffset}
          rel="next"
          withAriaLabel={false}
        >
          Volgende pagina
        </PageLink>
      ) : (
        <span />
      )}
    </nav>
  );
}

function PageLink({
  offset,
  route,
  current = false,
  children,
  onChangeOffset,
  withAriaLabel = true,
  ...attributes
}: {
  offset: number;
  route: Router;
  current?: boolean;
  children?: ReactNode;
  onChangeOffset?: OnChangeOffset;
  withAriaLabel?: boolean;
} & React.AnchorHTMLAttributes<HTMLAnchorElement>) {
  const page = offset + 1;
  return (
    <Link
      to={route(page)}
      aria-label={withAriaLabel ? `Pagina ${page}` : undefined}
      aria-current={current ? 'page' : undefined}
      onClick={
        onChangeOffset
          ? (e) => {
              e.preventDefault();
              onChangeOffset(offset);
            }
          : undefined
      }
      {...attributes}
    >
      {children ? children : page}
    </Link>
  );
}
